import { CHALLENGE_STATUS } from "../../../../services/User/User.interface";

interface OptionsWiew {
  props: {
    suggestedStartDateTime: number;
    minStartDateTime: number;
    startTimeText: string;
    disableDatePicker: boolean;
  };
  submitData: {
    challengeId: string;
    chapterId: string;
    challengeStatus: string;
  };
}

interface TodayAction {
  actionTitle: string;
  challengeTitle: string;
  reminderText: string;
  reminderTime: string;
  imgUrl: string;
  tags: Array<string>;
  actionDate: number;
  targetUrl: string;
}

interface Action {
  actionTitle: string;
  challengeTitle: string;
  tags: Array<string>;
  imgUrl: string;
  targetUrl: string;
  reminderText?: string;
  reminderTime: string;
}

interface PastAction {
  actionTitle: string;
  challengeTitle: string;
  challengeId: string;
  actionDate: number;
  tags: Array<string>;
  imgUrl: string;
  ageText?: string;
}

interface ActionsView {
  todayAction: TodayAction;
  actions: Array<Action>;
}

interface ChallengeTab {
  name: string;
  label: string;
}

type Labels<T extends string> = Record<T, string>;
type ItemLabels<T extends string, S extends string> = Labels<T> & {
  items: Labels<S>;
};

type ShellContent =
  | "descriptionTabLabel"
  | "actionTabLabel"
  | "whyTabLabel"
  | "chapterLabel"
  | "knowMoreBtnLabel"
  | "completionLabel";
type ShellCard = "doneLabel" | "skipLabel";
type ShellHeader = "newChallengeHeaderTitle" | "userChallengeHeaderTitle";
type ShellFooter = "acceptLabel" | "tryAgainLabel";

type OptionsShell = Record<
  "optionTitleLabel" | "recommendedLabel" | "validateLabel" | "cancelLabel",
  string
> & { startDateLabels: Record<"title" | "subTitle" | "value", string> } & {
  reminderLabels: Record<"title" | "subTitle" | "value", string>;
} & { monthsLabels: Array<string> };
interface ChallengeShell {
  contentLabels: Labels<ShellContent>;
  actionCardLabels: Labels<ShellCard>;
  headerLabels: Labels<ShellHeader>;
  footerLabels: Labels<ShellFooter>;
  options: OptionsShell;
  tabsLabels: Labels<"descriptionLabel" | "todoLabel" | "whyLabel">;
  difficultyLabels: ItemLabels<"title", "easy" | "simple" | "average" | "hard">;
  playersLabels: ItemLabels<"title", "solo" | "group" | "both">;
  frequencyLabels: ItemLabels<
    "title",
    "daily" | "weekly" | "biweekly" | "monthly" | "oneshot"
  >;
  whenLabels: ItemLabels<
    "title",
    "morning" | "noon" | "afternoon" | "diner" | "evening"
  >;
  durationLabels: ItemLabels<
    "title",
    "week" | "twoweeks" | "threeweeks" | "month" | "justonce"
  >;
  dayLabels: Labels<"todayShortLabel" | "todayLongLabel" | "tomorrowLabel">;
  statusLabels: Record<string, string>;
}

interface ChallengeView {
  challengeId: string;
  challengeStatus: string;
  coverImgUrl: string;
  optionsViewData: OptionsWiew;
  shell: ChallengeShell;

  segmentProps: {
    selected: "actions" | "description";
    tab1: ChallengeTab;
    tab2: ChallengeTab;
  };

  scheduleProps: {
    completion: number;
    category: string;
    challengeStatus: string;
    statusLabel: string;
    actions: Array<{
      status: string;
      dayText: string;
      isToday: boolean;
    }>;
  };

  descriptionProps: {
    specProps: {
      difficulty: string;
      schedule: string;
      when: string;
      players: string;
      duration: string;
    };
    challengeTitle: string;
    chapterTitle: string;
    chapterIndex: string;
    category: string;
    text: string;
  };
  actionsView: ActionsView;
}

interface ChallengeCard {
  title: string;
  category: string;
  imgUrl: string;
  targetUrl: string;
  status: CHALLENGE_STATUS;
}

interface ChallengeByCategoryView {
  challenges: Array<ChallengeCard>;
}

interface GameActionView {
  actionId: string;
  imgUrl: string;
  title: string;
  tag: string;
  content: string;
}

const defaultChallengesView = new Map<string, ChallengeView>();
const defaultChallengesByCategoryView = new Map<
  string,
  ChallengeByCategoryView
>();

const defaultActionView = new Map<string, GameActionView>();

export {
  ChallengeView,
  defaultChallengesView,
  defaultActionView,
  defaultChallengesByCategoryView,
  OptionsWiew,
  ActionsView,
  TodayAction,
  Action,
  ChallengeByCategoryView,
  ChallengeCard,
  GameActionView,
  PastAction,
};
